import { css, useTheme } from "@emotion/react"
import React from "react"
import { RestaurantListItem } from "../../models/restaurant"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { formatRestaurantOpeningTime } from "./utils"
import { Badge } from "../badge/Badge"
import { BodySubtextNormal, BodyTextRegular } from "../typography/text"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"
import { translationKeys } from "../../util/translationKeys"
import { BodyHeader1Semibold } from "../typography/body-headings"
import { RestaurantStatus } from "../restaurantStatus/RestaurantStatus"
import { CollectionMethod } from "../../models/collection-method"
import { CollectionMethodIdentifiers } from "../../util/collection-method-identifiers"

interface Props extends React.HTMLProps<HTMLDivElement> {
  restaurant: RestaurantListItem
  className: string
}

const notOpenForOrdersOverlayStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  padding: 16px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
`

const overlayTextStyle = css`
  text-align: center;
`

const badgesContainerStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
`

const infoContainerStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const addressStyle = css`
  margin-top: 16px;
`

export const RestaurantCard = ({ restaurant, ...rest }: Props) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const closingAt = restaurant?.closing_at
    ? moment(restaurant.closing_at).format("HH:mm")
    : null
  const opensAt = formatRestaurantOpeningTime(restaurant?.opening_at, t)
  const openForOrders = restaurant.open_for_orders

  const containerStyle = css`
    display: flex;
    height: 200px !important;
    max-height: 200px;
    justify-content: space-between;
    flex-direction: column;
    border-radius: 8px;
    overflow: hidden;
    background-size: cover;
    background-position: center;
    padding: 16px;
    position: relative;
    box-shadow: ${theme.shadows.cardRest};
    background-image: linear-gradient(
        180deg,
        rgba(196, 196, 196, 0) 0%,
        rgba(0, 0, 0, 0.6) 75%
      ),
      url(${restaurant.images[0]?.url});
    ${openForOrders &&
    `
    transition: transform 0.3s;
    cursor: pointer;
    &:hover {
      transform: scale(1.03);
    }
  `}
  `

  const returnCollectionMethodIcon = (collectionMethod: CollectionMethod) => {
    switch (collectionMethod) {
      case CollectionMethodIdentifiers.courier:
        return <FontAwesomeIcon icon={solid("person-biking")} />
      case CollectionMethodIdentifiers.pickup:
        return <FontAwesomeIcon icon={solid("person-walking")} />
      case CollectionMethodIdentifiers.eatIn:
        return <FontAwesomeIcon icon={solid("cutlery")} />
      default:
        return <></>
    }
  }

  const returnCollectionMethodText = (collectionMethod: CollectionMethod) => {
    switch (collectionMethod) {
      case CollectionMethodIdentifiers.courier:
        return t(translationKeys.venuedetailsDeliveryOptionsDelivery)
      case CollectionMethodIdentifiers.pickup:
        return t(translationKeys.venuedetailsDeliveryOptionsPickup)
      case CollectionMethodIdentifiers.eatIn:
        return t(translationKeys.comboOrderDetailsEatIn)
      default:
        return ""
    }
  }

  return (
    <div css={containerStyle} {...rest}>
      {!openForOrders && (
        <div css={notOpenForOrdersOverlayStyle}>
          <BodyHeader1Semibold
            css={overlayTextStyle}
            color={theme.colors.white}
          >
            {t(translationKeys.venueDetailsClosedOpeningSoon)}
          </BodyHeader1Semibold>
        </div>
      )}
      <div css={badgesContainerStyle}>
        {restaurant.shop_collection_methods?.map((collectionMethod, i) => (
          <Badge key={i} icon={returnCollectionMethodIcon(collectionMethod)}>
            <BodySubtextNormal color={theme.colors.white}>
              {returnCollectionMethodText(collectionMethod)}
            </BodySubtextNormal>
          </Badge>
        ))}
      </div>
      <div css={infoContainerStyle}>
        <BodyHeader1Semibold color={theme.colors.white}>
          {restaurant.title}
        </BodyHeader1Semibold>
        <BodyTextRegular color={theme.colors.white} css={addressStyle}>
          {restaurant.address}
        </BodyTextRegular>
        {openForOrders && (
          <RestaurantStatus
            opensAt={opensAt}
            closingAt={closingAt}
            isOpen={restaurant?.is_open}
          />
        )}
      </div>
    </div>
  )
}
